'use client';

import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/integrations/supabase';
import { Input } from '@/components/shared/ui/input';
import { Skeleton } from '@/components/shared/ui/skeleton';
import { Search, Building2, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/lib/integrations/language';

interface Business {
  id: string;
  name: string;
  logo_url: string | null;
  created_at: string;
  owner_name: string | null;
}

export function AdminBusinessSelector() {
  const { setAdminBusinessContext } = useAuth();
  const { isRTL } = useLanguage();
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Fetch businesses
  useEffect(() => {
    const fetchBusinesses = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`/api/admin/businesses?search=${encodeURIComponent(searchQuery)}`);

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || 'Failed to fetch businesses');
        }

        const data = await response.json();
        setBusinesses(data.businesses || []);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load businesses');
        setBusinesses([]);
      } finally {
        setIsLoading(false);
      }
    };

    // Debounce search
    const timeoutId = setTimeout(fetchBusinesses, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleSelectBusiness = async (businessId: string) => {
    await setAdminBusinessContext(businessId);
  };

  return (
    <div className="p-3 sm:p-4 lg:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-lg lg:text-xl font-semibold text-foreground">
          Admin Dashboard
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Select a business to monitor with full owner access
        </p>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className={cn(
          "absolute top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground",
          isRTL ? "right-3" : "left-3"
        )} />
        <Input
          placeholder="Search businesses..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={cn(isRTL ? "pr-10" : "pl-10")}
        />
      </div>

      {/* Error State */}
      {error && (
        <div className="card-bookdesk mb-4 !p-4">
          <p className="text-destructive text-sm">{error}</p>
        </div>
      )}

      {/* Business List */}
      <div className="card-bookdesk !p-0 overflow-hidden">

        {/* Loading State */}
        {isLoading && (
          <div className="divide-y divide-border">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="px-4 lg:px-5 py-4">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-10 w-10 rounded-lg flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <Skeleton className="h-4 w-32 mb-1" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Business Rows */}
        {!isLoading && businesses.length > 0 && (
          <div className="divide-y divide-border">
            {businesses.map((business) => (
              <button
                key={business.id}
                onClick={() => handleSelectBusiness(business.id)}
                className={cn(
                  "w-full px-4 lg:px-5 py-4 transition-colors",
                  "hover:bg-muted/50 active:bg-muted",
                  "focus:outline-none focus:bg-muted/50",
                  "text-left"
                )}
              >
                {/* Mobile Layout */}
                <div className="sm:hidden">
                  <div className="flex items-center gap-3">
                    {/* Logo */}
                    {business.logo_url ? (
                      <img
                        src={business.logo_url}
                        alt={business.name}
                        className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                        <Building2 className="h-5 w-5 text-muted-foreground" />
                      </div>
                    )}
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground truncate">{business.name}</p>
                      {business.owner_name && (
                        <p className="text-xs text-muted-foreground truncate">{business.owner_name}</p>
                      )}
                    </div>
                    <ChevronRight className={cn(
                      "h-5 w-5 text-muted-foreground flex-shrink-0",
                      isRTL && "rotate-180"
                    )} />
                  </div>
                </div>

                {/* Desktop Layout */}
                <div className={cn(
                  "hidden sm:flex sm:items-center sm:gap-3",
                  isRTL && "flex-row-reverse"
                )}>
                  {/* Business Info */}
                  <div className={cn("flex items-center gap-3 flex-1 min-w-0", isRTL && "flex-row-reverse")}>
                    {business.logo_url ? (
                      <img
                        src={business.logo_url}
                        alt={business.name}
                        className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                        <Building2 className="h-5 w-5 text-muted-foreground" />
                      </div>
                    )}
                    <div className={cn("min-w-0", isRTL && "text-right")}>
                      <p className="font-medium text-foreground truncate">{business.name}</p>
                      {business.owner_name && (
                        <p className="text-xs text-muted-foreground truncate">{business.owner_name}</p>
                      )}
                    </div>
                  </div>

                  {/* Arrow */}
                  <ChevronRight className={cn(
                    "h-5 w-5 text-muted-foreground flex-shrink-0",
                    isRTL && "rotate-180"
                  )} />
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && businesses.length === 0 && !error && (
          <div className="text-center py-12 px-4">
            <Building2 className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
            <p className="text-base text-muted-foreground">
              {searchQuery ? `No businesses match "${searchQuery}"` : 'No businesses found'}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {searchQuery ? 'Try a different search term' : 'Businesses will appear here once created'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
